import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import {
  IMediaFile,
  projectDataMediaThunks
} from 'store/features/projectData/thunks/media'
import { handleApiError } from 'store/utils'
import { ErrorKeys } from 'consts/errorKeys'
import { videoTypes } from 'consts'
import { IMediaItem, MediaStatus } from 'types/media'
import { extractFrameAtTimestamp } from 'services/video-thumbs/utils'
import { scrollToElementById } from 'utils'
import { calculateTotalMediaDuration } from 'utils/media'
import { useTimelineContext } from 'context/TimelineContext'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { useGetOverlappingVideoItem } from './useGetOverlappingVideoItem'
import { getPlaceholderMediaItem, getTimestampWithinMediaItem } from './utils'
import { FileRegistry } from 'utils/file-registry/FileRegistry'

const fileRegistry = FileRegistry.getInstance()

export const useFrameExtraction = () => {
  const canvasDefaultDimensions = useSelector(
    projectDataSelectors.defaultDimensions
  )
  const [isUploading, setIsUploading] = useState(false)
  const { getCurrentTimeImmediate, isPlaying } = useTimelineContext()
  const { isDurationAllowed } = useDurationLimitCheck()
  const { overlappingVideoItem: targetVideoItem } = useGetOverlappingVideoItem()
  const isButtonDisabled = !targetVideoItem || isPlaying || isUploading

  const uploadFrame = useCallback(
    async (
      frameBlob: Blob,
      frameBlobUrl: string,
      placeholderItem: IMediaItem
    ) => {
      try {
        setIsUploading(true)
        const frameFileName = `frame-${placeholderItem.id}.jpg`
        const frameFile = new File([frameBlob], frameFileName, {
          type: 'image/jpeg'
        })

        const uploadedMedias = (await dispatch(
          projectDataThunks.uploadMedias([
            {
              id: placeholderItem.id,
              type: 'image',
              file: frameFile
            }
          ])
        )) as IMediaFile[]

        if (!uploadedMedias[0]) {
          throw new Error('Failed to upload frame')
        }

        dispatch(
          projectDataThunks.updateMediaItemWithoutUndoSave({
            id: placeholderItem.id,
            updates: {
              meta: {
                ...placeholderItem.meta,
                path: uploadedMedias[0].filePath,
                originalWidth: uploadedMedias[0].width,
                originalHeight: uploadedMedias[0].height,
                status: MediaStatus.OK
              }
            }
          })
        )
      } catch {
        dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
        dispatch(projectDataThunks.removeVisualMediaItem(placeholderItem.id))
      } finally {
        fileRegistry.deleteFile(frameBlobUrl)
        setIsUploading(false)
      }
    },
    []
  )

  const handleExtractFrame = useCallback(
    async (freezedFrameTime: number) => {
      const videoPath = targetVideoItem?.meta?.path
      if (
        !targetVideoItem ||
        !videoTypes.includes(targetVideoItem.type) ||
        !videoPath
      ) {
        return
      }

      const clampedTimestamp = getTimestampWithinMediaItem(
        targetVideoItem,
        freezedFrameTime
      )

      const extractedFrameStartTime =
        targetVideoItem.startTime + targetVideoItem.duration

      // Extract the frame BEFORE adding the placeholder to the timeline so that
      // meta.path is never null when the engine picks up the item (otherwise the
      // engine tries to load an image from a null src and throws).
      let frameBlob: Blob
      try {
        frameBlob = await extractFrameAtTimestamp(videoPath, clampedTimestamp)
      } catch {
        dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
        return
      }
      const frameBlobUrl = fileRegistry.registerFile(frameBlob)

      const placeholderItem = getPlaceholderMediaItem(
        extractedFrameStartTime,
        canvasDefaultDimensions,
        targetVideoItem,
        frameBlobUrl
      )

      const totalNewDuration = calculateTotalMediaDuration([placeholderItem])
      if (!isDurationAllowed(totalNewDuration)) {
        fileRegistry.deleteFile(frameBlobUrl)
        return
      }

      dispatch(
        projectDataMediaThunks.adjustTimelineForNewItems(
          [placeholderItem],
          targetVideoItem.id
        )
      )
      scrollToElementById(placeholderItem.id)

      await uploadFrame(frameBlob, frameBlobUrl, placeholderItem)
    },
    [canvasDefaultDimensions, isDurationAllowed, uploadFrame, targetVideoItem]
  )

  const handleExtractLastFrame = useCallback(() => {
    if (!targetVideoItem) return
    const lastFrameTime = targetVideoItem?.startTime + targetVideoItem?.duration

    handleExtractFrame(lastFrameTime)
  }, [handleExtractFrame, targetVideoItem])

  const handleExtractCurrentFrame = useCallback(() => {
    const currentTimelineTime = getCurrentTimeImmediate()
    handleExtractFrame(currentTimelineTime)
  }, [getCurrentTimeImmediate, handleExtractFrame])

  return {
    handleExtractLastFrame,
    handleExtractCurrentFrame,
    isButtonDisabled
  }
}
